import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useStore } from "../../../app/stores/rootStore";

export default observer(function ReceiptPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { bookingStore } = useStore();
    const { selectedItem: booking, isLoaded } = bookingStore;

    useEffect(() => {
        if (id) {
            // Fetch booking by ID if not already in store (e.g., on page refresh)
            bookingStore.getItem(id);
        }
    }, [id, bookingStore]);

    if (!isLoaded && !booking) {
        return <div className="text-center py-5">Loading your receipt...</div>;
    }

    if (!booking) {
        return (
            <div className="container py-5 text-center">
                <h3>Receipt Not Found</h3>
                <button className="btn btn-primary mt-3" onClick={() => {                                
                                navigate(`/`);

                            }}>Return Home</button>                
            </div>
        );
    }

    return (
<section className="py-3 py-md-5 bg-light receipt-view">
    <style>{`
        .border-dashed-bottom { border-bottom: 1px dashed #dee2e6; }
        .item-image { width: 60px; height: 60px; object-fit: cover; border-radius: 10px; }
        
        /* Fix for very small screens */
        @media (max-width: 375px) {
            .item-title { max-width: 120px !important; }
            .receipt-header h1 { font-size: 1.5rem; }
        }

        @media print {
            body * { visibility: hidden; }
            .printable-receipt, .printable-receipt * { visibility: visible; }
            .printable-receipt { position: absolute; left: 0; top: 0; width: 100%; border: none !important; }
            .no-print { display: none !important; }
        }
    `}</style>

    <div className="container-fluid container-md">
        <div className="row justify-content-center">
            <div className="col-12 col-lg-8">
                <div className="card shadow-sm border-0 p-3 p-md-5 bg-white printable-receipt rounded-4">
                    
                    {/* Header: Centered & Scaled */}
                    <div className="text-center mb-4 mb-md-5 receipt-header">
                        <div className="display-4 text-success mb-2 animate__animated animate__zoomIn">
                            <i className="bi bi-check-circle-fill"></i>
                        </div>
                        <h1 className="fw-bold h2">Order Confirmed!</h1>
                        <p className="text-muted small">A copy has been sent to your email.</p>
                    </div>

                    {/* Metadata: Use row-cols-2 for consistent alignment */}
                    <div className="row g-3 mb-4 pb-4 border-bottom">
                        <div className="col-7">
                            <h6 className="text-uppercase text-muted x-small fw-bold mb-1" style={{fontSize: '0.7rem'}}>Receipt Number</h6>
                            <p className="fw-bold text-primary mb-0 small text-break">{booking.receiptNumber}</p>
                        </div>
                        <div className="col-5 text-end">
                            <h6 className="text-uppercase text-muted x-small fw-bold mb-1" style={{fontSize: '0.7rem'}}>Order Date</h6>
                            <p className="mb-0 small">
                                {booking.orderDate?.toDate ? booking.orderDate.toDate().toLocaleDateString() : new Date().toLocaleDateString()}
                            </p>
                        </div>
                    </div>

                    {/* Order Items: Responsive Flex Layout */}
                    <div className="order-items-list">
                        <h6 className="text-uppercase text-muted x-small fw-bold mb-3" style={{fontSize: '0.75rem'}}>Order Summary</h6>
                        {booking.items.map((item, idx) => (
                            <div key={idx} className="d-flex align-items-start gap-2 gap-md-3 py-3 border-dashed-bottom">
                                <img 
                                    src={item.mainImageUrl || '/api/placeholder/60/60'} 
                                    alt={item.title} 
                                    className="item-image border"
                                />
                                
                                <div className="flex-grow-1 min-w-0">
                                    <span className="fw-bold d-block text-truncate item-title" style={{maxWidth: '180px'}}>{item.title}</span>
                                    <small className="text-muted d-block text-truncate small">{item.brand}</small>
                                    <small className="fw-medium text-dark">Qty: {item.quantity}</small>
                                </div>
                                
                                <div className="text-end flex-shrink-0">
                                    <span className="fw-bold small">Rs. {(item.productPrice * item.quantity).toLocaleString()}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Totals: Right-aligned on desktop, Full-width on mobile */}
                    <div className="row justify-content-end mt-4">
                        <div className="col-12 col-md-6 col-lg-5">
                            <div className="d-flex justify-content-between mb-2 small">
                                <span className="text-muted">Subtotal</span>
                                <span>Rs. {(booking.subtotal || (booking.total + (booking.discountAmount || 0))).toLocaleString()}</span>
                            </div>
                            
                            {booking.discountAmount > 0 && (
                                <div className="d-flex justify-content-between mb-2 text-success small">
                                    <span>Discount</span>
                                    <span className="fw-bold">- Rs. {booking.discountAmount.toLocaleString()}</span>
                                </div>
                            )}

                            <div className="d-flex justify-content-between mb-3 text-success small">
                                <span className="text-muted">Shipping</span>
                                <span className="fw-bold">FREE</span>
                            </div>

                            <div className="d-flex justify-content-between border-top pt-3 h4 fw-bold text-dark">
                                <span>Total</span>
                                <span>Rs. {booking.total.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Actions: Full width on mobile */}
                    <div className="mt-5 d-grid d-sm-flex gap-3 justify-content-sm-center no-print">
                        <button className="btn btn-dark btn-lg rounded-pill px-4 order-2 order-sm-1" onClick={() => window.print()}>
                            <i className="bi bi-printer me-2"></i> Print
                        </button>
                        <Link to="/catalog" className="btn btn-outline-primary btn-lg rounded-pill px-4 order-1 order-sm-2">
                            Continue Shopping
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>
    );
});